/**
 * Dataverse Web API adapter — production path for Azure SWA hosting.
 *
 * Uses OOB tables only:
 *   msdyn_timeentry, msdyn_workorder, msdyn_project, msdyn_projecttask,
 *   msdyn_transactioncategory, bookableresource, bookableresourcecategory,
 *   account, annotation.
 *
 * Auth token is acquired by the caller (AuthProvider) and threaded through
 * via the `getToken` callback so this file is free of MSAL coupling.
 *
 * One source of environment risk: option-set values and a handful of lookup
 * schema names below. The defaults match the OOB Field Service / Project
 * Operations install — verify them in the maker portal and tweak the
 * constants here if your environment overrides them.
 */
import { auditSubject } from "@/utils/audit";
import { workOrderStatus } from "@/utils/workOrderStatus";
import { durationMinutes } from "@/utils/duration";
import { compareText } from "@/utils/sort";
import { assertStaffEdit, assertStaffResource } from "@/utils/staffEntries";
import { staffAuditNotes } from "@/utils/staffAudit";
// ---- Option-set values ----
// These integer codes are the OOB values published by the global choice
// set `msdyn_timeentrystatus` (Time Entry Status). The custom column
// `crb7a_approvalstatus` links to that global choice (see
// docs/dataverse-setup.md §8), so values are inherited — not redefined —
// and a flip-back to `msdyn_entrystatus` is a value-set match by design.
// Two OOB labels we don't currently surface in the UI:
//   Cancelled       = 192354320 (different range)
//   Recall Requested = 192350004
// `STATUS_FROM_CODE` lookups for those return undefined and are widened
// to "Draft" by the `?? "Draft"` fallback in `toTimeEntry`.
const STATUS_TO_CODE = {
    Draft: 192350000,
    Returned: 192350001,
    Approved: 192350002,
    Submitted: 192350003,
};
// v0.5.31 — status lifecycle column.
//
// Originally pointed at the OOB `msdyn_entrystatus`, but the OOB Project
// Operations `TimeEntryStateValidator` plugin rejects Draft→Submitted on
// any entry that doesn't fit its state-machine (no work order, no project
// task, etc.). The v0.5.27–v0.5.30 chain attempted to satisfy the plugin
// with a "General Work" project + task bridge, but every fix either (a)
// shifted the problem to the next precondition or (b) polluted the
// regarding link (UI started showing "General Work" instead of the
// customer). v0.5.31 sidesteps the plugin entirely by routing reads and
// writes through a custom Choice column the plugin doesn't watch.
//
// The custom column must be created on `msdyn_timeentry` with the same
// integer values as `STATUS_TO_CODE` above — see
// `docs/dataverse-setup.md` § "Custom approval-status column" for the
// runbook. No data backfill is needed: existing rows with a null value
// are treated as Draft in the read path and via the filter helper below.
//
// **Flip-back path (intentional one-liner):** when the OOB lifecycle
// becomes usable again (validator disabled, PO Lite installed, or a new
// state-machine shape adopted), change `STATUS_COLUMN` back to
// `"msdyn_entrystatus"`. The `if (STATUS_COLUMN === "msdyn_entrystatus")`
// guard inside `submitEntries` re-enables the General Work bridge
// automatically; `ensureGeneralWorkProjectAndTask` and friends remain
// in source for that day.
const STATUS_COLUMN = "crb7a_approvalstatus";
// Filter clause that matches an OData status equality with a fallback for
// legacy rows where the custom column was added after the entry was
// created — those read as `null` from Dataverse, which `eq <code>` does
// not match. We treat null as Draft so old entries don't vanish from the
// drafts view. When `STATUS_COLUMN === "msdyn_entrystatus"` the OOB
// column is always populated, so the null branch is a no-op there.
function statusFilter(status) {
    const code = STATUS_TO_CODE[status];
    if (status === "Draft" && STATUS_COLUMN !== "msdyn_entrystatus") {
        return `(${STATUS_COLUMN} eq ${code} or ${STATUS_COLUMN} eq null)`;
    }
    return `${STATUS_COLUMN} eq ${code}`;
}
// msdyn_timeentry.msdyn_type — backed by the OOB global choice
// `msdyn_timeentrytype`. These integers are NOT sequential and are NOT in the
// 192350xxx band for every member: Work / Absence / Vacation sit at
// 192350000-002, but On Break / Travel / Overtime live in the 192354/192355
// bands. Confirmed against the live raines.crm6 maker portal and Microsoft
// Learn (2026-06-22). The previous sequential guess (Break=…001, Travel=…002,
// Leave=…003) silently logged Travel as Vacation/Time-off — keep keys matching
// the EntryType union so the picker and the wire codes stay in lockstep.
const TYPE_TO_CODE = {
    Work: 192350000,
    Absence: 192350001,
    Vacation: 192350002,
    "On Break": 192355000,
    Travel: 192355001,
    Overtime: 192354320,
};
// msdyn_transactioncategory.msdyn_billingtype — the OOB column that drives
// billable-vs-not for time entries. Values observed on raines.crm6.dynamics.com:
//   192350000 NonChargeable · 192350001 Chargeable · 192350002 Complimentary
// (Verify under make.powerapps.com → Tables → Transaction Category → Columns →
// Billing Type. Note the integer order is not Chargeable-first.)
const TC_BILLING_FROM_CODE = {
    192350000: "NonChargeable",
    192350001: "Chargeable",
    192350002: "Complimentary",
};
const STATUS_FROM_CODE = invert(STATUS_TO_CODE);
const TYPE_FROM_CODE = invert(TYPE_TO_CODE);
// Time Source — source-type values on the `msdyn_timeentrysetting` records,
// backed by the OOB global choice `msdyn_timeentrysourcetype`. Confirmed
// against the live raines.crm6 records on 2026-06-23. The time entry links to a
// Time Source *record* (lookup), and that record carries one of these source
// types; one record is flagged default (Project Service here) and Dataverse
// applies it whenever the lookup is left empty — which is why work-order
// entries were logging as Project Service.
const TIME_SOURCE_TYPE = {
    FieldService: 192354000,
    ProjectService: 192351000,
};
// Session cache: msdyn_sourcetype code → msdyn_timeentrysetting record id.
// Resolved once per session (the table has two rows). We key off the stable
// source-type value rather than a hardcoded record GUID so the mapping survives
// a record rebuild / re-import. Cleared on full page reload.
let TIME_SOURCE_SETTING_IDS = null;
// UI-side rate book for resource roles. bookableresourcecategory has no OOB
// hourly-rate column, so role rate lives client-side keyed by display name.
// Used as a fallback in lineAmount() when a service type has no rate set.
const CATEGORY_RATES = {
    "Senior Consultant": 220,
    Consultant: 180,
    "Field Technician": 140,
    "Project Manager": 200,
};
// Session-cached availability of the custom rate column. We try the richer
// $select first; on a 400 we strip `crb7a_rate` and remember to skip it for
// the rest of the session so each refresh doesn't pay the failed request.
// (`_msdyn_defaultunit_value` is OOB so it always reads — no probing needed.)
// Cleared on full page reload.
let TC_RATE_COLUMNS_PRESENT = null;
// Session-cached navigation property names for write-time @odata.bind. The
// Web API rejects any guess that isn't the literal published nav property
// name, and conventions vary by how the column was originally created — so
// we ask the metadata once per (entity, fk) pair and cache the answer for
// the rest of the session. Discovered via
// `EntityDefinitions(LogicalName='...')/ManyToOneRelationships?$filter=...`
// which returns the exact `ReferencingEntityNavigationPropertyName`.
//   project_customer       — msdyn_project.msdyn_customer → account
//                            (v0.5.29.1; needed to create General Work projects)
//   projecttask_project    — msdyn_projecttask.msdyn_project → msdyn_project
//                            (v0.5.30; needed to create the default task
//                            under each General Work project)
const NAV_PROP_CACHE = {};
export class DataverseAdapter {
    constructor(cfg) {
        Object.defineProperty(this, "cfg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: cfg
        });
        Object.defineProperty(this, "apiBase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.apiBase = `${cfg.baseUrl.replace(/\/+$/, "")}/api/data/v9.2`;
    }
    // ---------- Lookups ----------
    async listAccounts(activeOnly = false) {
        // OOB column is `industrycode` (option-set), not `industry`. We read the
        // formatted value annotation so the UI keeps showing a string label.
        // `statecode` is the standard active/inactive flag — 0 active, 1 inactive.
        // `crb7a_whmcsid` is a custom text column holding the WHMCS client ID
        // (verified 2026-05-24); null/empty when the account isn't linked to WHMCS.
        const filter = activeOnly ? `&$filter=statecode eq 0` : "";
        const data = await this.get(`/accounts?$select=accountid,name,industrycode,statecode,crb7a_whmcsid&$orderby=name asc&$top=500${filter}`);
        return data.value.map((a) => ({
            accountid: a.accountid,
            name: a.name,
            industry: a["industrycode@OData.Community.Display.V1.FormattedValue"] ?? undefined,
            statecode: a.statecode,
            crb7a_whmcsid: a.crb7a_whmcsid ?? null,
        })).sort((a, b) => compareText(a.name, b.name));
    }
    async listWorkOrders() {
        const data = await this.get(`/msdyn_workorders?$select=msdyn_workorderid,msdyn_name,_msdyn_serviceaccount_value,msdyn_systemstatus&$orderby=msdyn_name%20desc&$top=500`);
        return data.value.map((w) => ({
            msdyn_workorderid: w.msdyn_workorderid,
            msdyn_name: w.msdyn_name,
            msdyn_serviceaccount: w._msdyn_serviceaccount_value,
            msdyn_systemstatus: workOrderStatus(w.msdyn_systemstatus, w["msdyn_systemstatus@OData.Community.Display.V1.FormattedValue"]),
        })).sort((a, b) => compareText(b.msdyn_name, a.msdyn_name));
    }
    async listProjects() {
        const data = await this.get(`/msdyn_projects?$select=msdyn_projectid,msdyn_subject,_msdyn_customer_value,_msdyn_projectmanager_value&$orderby=msdyn_subject%20asc&$top=500`);
        return data.value.map((p) => ({
            msdyn_projectid: p.msdyn_projectid,
            msdyn_subject: p.msdyn_subject,
            msdyn_customer: p._msdyn_customer_value ?? undefined,
            msdyn_projectmanager: p._msdyn_projectmanager_value ?? undefined,
            ownerName: p["_msdyn_projectmanager_value@OData.Community.Display.V1.FormattedValue"],
        })).sort((a, b) => compareText(a.msdyn_subject, b.msdyn_subject));
    }
    async listProjectTasks(projectId) {
        const data = await this.get(`/msdyn_projecttasks?$select=msdyn_projecttaskid,msdyn_subject,_msdyn_project_value&$filter=_msdyn_project_value eq ${projectId}&$orderby=msdyn_subject%20asc&$top=500`);
        return data.value.map((t) => ({
            msdyn_projecttaskid: t.msdyn_projecttaskid,
            msdyn_subject: t.msdyn_subject,
            msdyn_project: t._msdyn_project_value,
        })).sort((a, b) => compareText(a.msdyn_subject, b.msdyn_subject));
    }
    async listTransactionCategories() {
        // No $select — small table, and the display column varies between
        // Project Service Automation (msdyn_description) and newer Project
        // Operations installs (msdyn_name). Fall back across both.
        // msdyn_billingtype is OOB and drives the billable flag for time entries
        // (see utils/billing.ts). Default to "NonChargeable" when missing so an
        // unmapped row reads as non-billable rather than silently billable.
        //
        // `crb7a_rate` is the only CUSTOM column on this table — the per-unit
        // price. The unit itself (hour vs minute) is read from the OOB lookup
        // `msdyn_defaultunit` (→ uom table), so a previous `crb7a_rateunit`
        // choice column has been retired (v0.5.21). The full OOB Project
        // Operations price-list tables (msdyn_categorypricelevels,
        // msdyn_pricelistitems) aren't installed on raines.crm6.dynamics.com —
        // this is the Lite-equivalent path until PO Lite is deployed (tracked
        // in PLAN.md → Deferred work).
        //
        // Defensive fetch: try with the custom column first; if the environment
        // hasn't had it created yet the request 400s — retry without it and
        // cache that for the session so further calls skip straight to the
        // baseline query. Once the column exists this branch is a single
        // happy-path. `_msdyn_defaultunit_value` is OOB so it always reads.
        const data = await this.fetchTransactionCategories();
        return data.value.map((c) => {
            // The unit display name comes back inline via the FormattedValue
            // annotation that `get()` requests for every call. We only care about
            // "Minute" — anything else (Hour, Day, blank) is treated as Hour by
            // lineAmount, which is the sensible default for a services business.
            const unitName = c["_msdyn_defaultunit_value@OData.Community.Display.V1.FormattedValue"];
            const rateUnit = c._msdyn_defaultunit_value
                ? unitName === "Minute"
                    ? "Minute"
                    : "Hour"
                : undefined;
            return {
                msdyn_transactioncategoryid: c.msdyn_transactioncategoryid,
                msdyn_description: c.msdyn_description ?? c.msdyn_name ?? "",
                msdyn_billingtype: TC_BILLING_FROM_CODE[c.msdyn_billingtype] ?? "NonChargeable",
                rate: typeof c.crb7a_rate === "number" ? c.crb7a_rate : undefined,
                rateUnit,
            };
        }).sort((a, b) => compareText(a.msdyn_description, b.msdyn_description));
    }
    async fetchTransactionCategories() {
        // Baseline includes the OOB Default Unit lookup — that's always readable.
        // The custom `crb7a_rate` is the only column we probe for.
        //
        // NOTE: `msdyn_description` is the PSA-era label column; modern Project
        // Operations installs (including raines.crm6.dynamics.com) only have
        // `msdyn_name`. v0.5.21 reintroduced the PSA name in the explicit $select
        // and 400'd outright — see v0.5.22 fix. Mapping below still keeps the
        // `?? msdyn_name` fallback in case a PSA env ever resurfaces.
        const baseUrl = `/msdyn_transactioncategories?$select=msdyn_transactioncategoryid,msdyn_name,msdyn_billingtype,_msdyn_defaultunit_value&$orderby=msdyn_name%20asc&$top=200`;
        if (TC_RATE_COLUMNS_PRESENT === false)
            return this.get(baseUrl);
        const richUrl = `/msdyn_transactioncategories?$select=msdyn_transactioncategoryid,msdyn_name,msdyn_billingtype,_msdyn_defaultunit_value,crb7a_rate&$orderby=msdyn_name%20asc&$top=200`;
        try {
            const out = await this.get(richUrl);
            TC_RATE_COLUMNS_PRESENT = true;
            return out;
        }
        catch (err) {
            // Any failure to read the custom column — column missing, security role,
            // typo on the publisher prefix — degrades quietly to the baseline query.
            // Log once so it's discoverable in dev tools.
            if (TC_RATE_COLUMNS_PRESENT === null) {
                console.warn("[Dataverse] crb7a_rate not readable on msdyn_transactioncategory — falling back to role-rate. Error:", err);
            }
            TC_RATE_COLUMNS_PRESENT = false;
            return this.get(baseUrl);
        }
    }
    async listResources() {
        // bookableresource has no single resource-category FK — categories are a
        // many-to-many through `bookableresourcecategoryassns`. The entry-level
        // `_msdyn_resourcecategory_value` on msdyn_timeentry is the one that drives
        // the billing rate per logged entry, so we don't need a default category
        // on the resource itself. Leave `resourceCategoryId` undefined.
        const data = await this.get(`/bookableresources?$select=bookableresourceid,name,_userid_value&$expand=UserId($select=businessunitid)&$orderby=name%20asc&$top=500`);
        return data.value.map((r) => ({
            bookableresourceid: r.bookableresourceid,
            name: r.name,
            userId: r._userid_value ?? undefined,
            businessUnitId: r.UserId?._businessunitid_value ?? undefined,
            resourceCategoryId: undefined,
        })).sort((a, b) => compareText(a.name, b.name));
    }
    async listResourceCategories() {
        const data = await this.get(`/bookableresourcecategories?$select=bookableresourcecategoryid,name&$orderby=name%20asc&$top=200`);
        return data.value.map((c) => ({
            bookableresourcecategoryid: c.bookableresourcecategoryid,
            name: c.name,
            hourlyRate: CATEGORY_RATES[c.name] ?? 0,
        })).sort((a, b) => compareText(a.name, b.name));
    }
    async listDirectReports(managerUserId) {
        const reports = await this.getAllRows(`/systemusers?$select=systemuserid&$filter=${encodeURIComponent(`_parentsystemuserid_value eq ${managerUserId}`)}`);
        if (reports.length === 0)
            return [];
        const users = reports.map((row) => `_userid_value eq ${row.systemuserid}`).join(" or ");
        const resources = await this.getAllRows(`/bookableresources?$select=bookableresourceid,name,_userid_value&$filter=${encodeURIComponent(users)}&$orderby=name%20asc`);
        return resources.map((row) => ({
            bookableresourceid: row.bookableresourceid,
            name: row.name,
            userId: row._userid_value,
        })).sort((a, b) => compareText(a.name, b.name));
    }
    async listStaffTimeEntries(managerUserId) {
        const reports = await this.listDirectReports(managerUserId);
        if (reports.length === 0)
            return [];
        const resources = reports.map((row) => `_msdyn_bookableresource_value eq ${row.bookableresourceid}`).join(" or ");
        const filter = `(${resources}) and (${statusFilter("Draft")} or ${statusFilter("Submitted")} or ${statusFilter("Returned")}) and (crb7a_whmcsposted eq false or crb7a_whmcsposted eq null)`;
        const rows = await this.getAllRows(`/msdyn_timeentries?$select=${TIMEENTRY_FIELDS}&$filter=${encodeURIComponent(filter)}&$orderby=msdyn_start%20desc`);
        return rows.map(toTimeEntry);
    }
    async createStaffTimeEntry(managerUserId, input) {
        const reports = await this.listDirectReports(managerUserId);
        const employee = assertStaffResource(reports, input.bookableresource);
        const id = crypto.randomUUID();
        const draft = { ...input, msdyn_timeentryid: id, msdyn_duration: durationMinutes(input), msdyn_entryStatus: "Draft" };
        const payload = { ...toCreatePayload(draft), msdyn_timeentryid: id };
        await this.applyTimeSource(payload, input.regardingobjectid);
        return this.writeStaffTransaction("POST", "/msdyn_timeentries", payload, id, "Created on behalf", staffAuditNotes(employee.name, undefined, draft));
    }
    async updateStaffTimeEntry(managerUserId, id, patch) {
        const reports = await this.listDirectReports(managerUserId);
        const row = await this.get(`/msdyn_timeentries(${id})?$select=${TIMEENTRY_FIELDS}`);
        this.assertStaffStatus(row[STATUS_COLUMN]);
        const entry = toTimeEntry(row);
        const employee = assertStaffEdit(reports, entry, patch);
        const etag = row["@odata.etag"];
        if (typeof etag !== "string") {
            throw new Error("Could not verify the staff entry version. Refresh Staff Entries before saving.");
        }
        const payload = toPatchPayload({
            ...patch, bookableresource: entry.bookableresource, msdyn_entryStatus: "Draft",
        });
        if (patch.regardingobjectid)
            await this.applyTimeSource(payload, patch.regardingobjectid);
        return this.writeStaffTransaction("PATCH", `/msdyn_timeentries(${id})`, payload, id, "Edited on behalf", staffAuditNotes(employee.name, entry, {
            ...entry, ...patch, bookableresource: entry.bookableresource, msdyn_entryStatus: "Draft",
        }), etag);
    }
    async submitStaffTimeEntry(managerUserId, id, resubmit = false) {
        const reports = await this.listDirectReports(managerUserId);
        const row = await this.get(`/msdyn_timeentries(${id})?$select=${TIMEENTRY_FIELDS}`);
        this.assertStaffStatus(row[STATUS_COLUMN]);
        const entry = toTimeEntry(row);
        const employee = assertStaffEdit(reports, entry, {});
        if (entry.msdyn_entryStatus !== "Draft")
            throw new Error("Only Draft staff entries can be submitted. Refresh Staff Entries.");
        const etag = row["@odata.etag"];
        if (typeof etag !== "string") {
            throw new Error("Could not verify the staff entry version. Refresh Staff Entries before submitting.");
        }
        const notes = staffAuditNotes(employee.name, entry, {
            ...entry, msdyn_entryStatus: "Submitted",
        });
        await this.submitEntries([id], { [id]: etag }, {
            [id]: { subject: resubmit ? "Resubmitted on behalf" : "Submitted on behalf", notes },
        });
    }
    assertStaffStatus(status) {
        if (status != null && status !== STATUS_TO_CODE.Draft && status !== STATUS_TO_CODE.Submitted && status !== STATUS_TO_CODE.Returned) {
            throw new Error("Only Draft, Submitted or Returned staff entries can be changed. Refresh Staff Entries.");
        }
    }
    async listBookings(workOrderId) {
        // FS schedule-board appointments. Filter by work order when one is given
        // so the picker in the entry dialogs only shows bookings under the
        // currently-selected WO (bookings nest under work orders in FS).
        const filter = workOrderId
            ? `&$filter=_msdyn_workorder_value eq ${workOrderId}`
            : "";
        const data = await this.get(`/bookableresourcebookings?$select=bookableresourcebookingid,name,starttime,endtime,_msdyn_workorder_value,_resource_value&$orderby=starttime desc&$top=500${filter}`);
        return data.value.map((b) => ({
            bookableresourcebookingid: b.bookableresourcebookingid,
            name: b.name ?? "",
            workOrderId: b._msdyn_workorder_value ?? undefined,
            resourceId: b._resource_value ?? undefined,
            starttime: b.starttime ?? undefined,
            endtime: b.endtime ?? undefined,
        }));
    }
    // ---------- Time entries ----------
    async listTimeEntries(filter = {}) {
        const conds = [];
        if (filter.resourceId)
            conds.push(`_msdyn_bookableresource_value eq ${filter.resourceId}`);
        if (filter.status)
            conds.push(statusFilter(filter.status));
        if (filter.from)
            conds.push(`msdyn_start ge ${filter.from}`);
        if (filter.to)
            conds.push(`msdyn_start le ${filter.to}`);
        const params = [
            `$select=${TIMEENTRY_FIELDS}`,
            conds.length ? `$filter=${encodeURIComponent(conds.join(" and "))}` : "",
            `$orderby=msdyn_start desc`,
            `$top=500`,
        ]
            .filter(Boolean)
            .join("&");
        const data = await this.get(`/msdyn_timeentries?${params}`);
        let rows = data.value.map(toTimeEntry);
        // accountId requires joining via work order / project — do it client-side.
        if (filter.accountId) {
            const [wos, projs] = await Promise.all([this.listWorkOrders(), this.listProjects()]);
            const woAccount = new Map(wos.map((w) => [w.msdyn_workorderid, w.msdyn_serviceaccount]));
            const projAccount = new Map(projs.map((p) => [p.msdyn_projectid, p.msdyn_customer]));
            rows = rows.filter((r) => clientIdFor(r, woAccount, projAccount) === filter.accountId);
        }
        // search: OData contains() is restricted on some envs; apply client-side.
        if (filter.search) {
            const q = filter.search.toLowerCase();
            rows = rows.filter((r) => r.msdyn_description.toLowerCase().includes(q) ||
                r.regardingobjectid?.display.toLowerCase().includes(q));
        }
        return rows;
    }
    async createTimeEntry(input) {
        const payload = toCreatePayload(input);
        await this.applyTimeSource(payload, input.regardingobjectid);
        const created = await this.post(`/msdyn_timeentries`, payload);
        return toTimeEntry(created);
    }
    async updateTimeEntry(id, patch) {
        // Defense in depth: UI hides Edit on Approved rows, but the adapter
        // pre-checks status here so an out-of-band call (e.g. timer stop racing
        // an approval) can't silently mutate a locked entry. Approver-driven
        // transitions go through approveEntries / returnEntries which bypass
        // this guard intentionally.
        await this.assertNotApproved(id);
        const payload = toPatchPayload(patch);
        // Re-derive Time Source only when the regarding changes (parity with
        // MockAdapter) — switching to/from a Work Order flips Field Service ↔
        // Project Service.
        if (patch.regardingobjectid)
            await this.applyTimeSource(payload, patch.regardingobjectid);
        const updated = await this.patch(`/msdyn_timeentries(${id})`, payload);
        return toTimeEntry(updated);
    }
    async deleteTimeEntry(id) {
        await this.assertNotApproved(id);
        await this.del(`/msdyn_timeentries(${id})`);
    }
    async markWhmcsPosted(id, billableId, postedOn) {
        // Deliberately skip assertNotApproved — only Approved entries are eligible
        // for WHMCS posting in the first place. The UI gates that; the column
        // writes here record the result.
        const updated = await this.patch(`/msdyn_timeentries(${id})`, {
            crb7a_whmcsposted: true,
            crb7a_whmcsbillableitemid: billableId,
            crb7a_whmcspostedon: postedOn,
        });
        return toTimeEntry(updated);
    }
    async assertNotApproved(id) {
        const row = await this.get(`/msdyn_timeentries(${id})?$select=${STATUS_COLUMN}`);
        if (row?.[STATUS_COLUMN] === STATUS_TO_CODE.Approved) {
            throw new Error("Approved time entries are read-only. Ask your approver to return the entry first.");
        }
    }
    // v0.6.1.2 — WHMCS-posting gate for the return path. Once a time entry has
    // been posted to WHMCS (`crb7a_whmcsposted = true`) the billing line exists
    // in the customer's account in WHMCS, so flipping the entry back to
    // Returned/Draft on our side would leave the two systems out of sync. We
    // pre-fetch the posted flag for all candidate ids in one filtered query
    // and throw before any PATCH runs, so the bulk operation is all-or-nothing.
    async assertNoneWhmcsPosted(ids) {
        if (ids.length === 0)
            return;
        const idClause = ids.map((id) => `msdyn_timeentryid eq ${id}`).join(" or ");
        const data = await this.get(`/msdyn_timeentries?$select=msdyn_timeentryid,crb7a_whmcsposted&$filter=${encodeURIComponent(idClause)}&$top=500`);
        const posted = data.value
            .filter((r) => r.crb7a_whmcsposted === true)
            .map((r) => r.msdyn_timeentryid);
        if (posted.length > 0) {
            throw new Error(posted.length === ids.length
                ? "These entries have already been posted to WHMCS and can't be returned. Reverse the billing item in WHMCS first."
                : `${posted.length} of ${ids.length} entries have been posted to WHMCS and can't be returned. Reverse the billing item in WHMCS first.`);
        }
    }
    // ---------- Approval actions ----------
    async approveEntries(ids, audit) {
        const subject = auditSubject("approved", audit.delegationRole);
        await Promise.all(ids.map(async (id) => {
            await this.patch(`/msdyn_timeentries(${id})`, {
                [STATUS_COLUMN]: STATUS_TO_CODE.Approved,
            });
            await this.writeAnnotation(id, subject, audit.comment ?? "");
        }));
    }
    async returnEntries(ids, audit, comment) {
        // v0.6.1.2 — defense in depth: refuse to flip status when any candidate
        // has already been posted to WHMCS. The UI hides the affordance, but the
        // adapter still enforces it so out-of-band callers (REST, console, future
        // bulk tools) can't desync the two systems.
        await this.assertNoneWhmcsPosted(ids);
        const subject = auditSubject("returned", audit.delegationRole);
        await Promise.all(ids.map(async (id) => {
            await this.patch(`/msdyn_timeentries(${id})`, {
                [STATUS_COLUMN]: STATUS_TO_CODE.Returned,
            });
            await this.writeAnnotation(id, subject, comment);
        }));
    }
    async selfReturnEntries(ids, audit) {
        // Same WHMCS-posted guard as returnEntries — a self-approved entry that
        // somehow made it onto a WHMCS invoice can't be silently rolled back.
        await this.assertNoneWhmcsPosted(ids);
        const subject = auditSubject("self-returned");
        await Promise.all(ids.map(async (id) => {
            await this.patch(`/msdyn_timeentries(${id})`, {
                [STATUS_COLUMN]: STATUS_TO_CODE.Returned,
            });
            await this.writeAnnotation(id, subject, audit.comment ?? "");
        }));
    }
    async selfApproveEntries(ids, audit) {
        const subject = auditSubject("self-approved");
        await Promise.all(ids.map(async (id) => {
            await this.patch(`/msdyn_timeentries(${id})`, {
                [STATUS_COLUMN]: STATUS_TO_CODE.Approved,
            });
            await this.writeAnnotation(id, subject, audit.comment ?? "");
        }));
    }
    async submitEntries(ids, expectedVersions = {}, staffAudit = {}) {
        if (ids.length === 0)
            return;
        // ---- General Work project linking (flip-back path only) ----
        // The OOB Project Operations / Field Service TimeEntryStateValidator
        // plugin requires either _msdyn_workorder_value or _msdyn_project_value
        // (and a project task) to be set for Draft→Submitted on the OOB status
        // column. v0.5.31 routes status through the custom STATUS_COLUMN which
        // the plugin doesn't watch, so the bridge is a no-op on that path —
        // skipped by the guard below. When/if STATUS_COLUMN flips back to
        // `msdyn_entrystatus` the bridge re-engages automatically without any
        // other code change. Helpers (`ensureGeneralWorkProjectAndTask`,
        // `ensureDefaultProjectTask`, `discoverNavProperty`) stay in source for
        // that day.
        if (STATUS_COLUMN === "msdyn_entrystatus") {
            const idClause = ids.map((id) => `msdyn_timeentryid eq ${id}`).join(" or ");
            const stateRows = await this.get(`/msdyn_timeentries?$select=msdyn_timeentryid,_msdyn_workorder_value,_msdyn_project_value,_crb7a_billableaccount_value&$filter=${encodeURIComponent(idClause)}&$top=500`);
            const accountDirect = new Map(); // accountId → entry ids
            for (const r of stateRows.value) {
                if (!r._msdyn_workorder_value && !r._msdyn_project_value && r._crb7a_billableaccount_value) {
                    const acct = r._crb7a_billableaccount_value;
                    const arr = accountDirect.get(acct) ?? [];
                    arr.push(r.msdyn_timeentryid);
                    accountDirect.set(acct, arr);
                }
            }
            if (accountDirect.size > 0) {
                const linkByAccount = new Map();
                await Promise.all(Array.from(accountDirect.keys()).map(async (accountId) => {
                    const link = await this.ensureGeneralWorkProjectAndTask(accountId);
                    linkByAccount.set(accountId, link);
                }));
                const linkOps = [];
                for (const [accountId, entryIds] of accountDirect) {
                    const link = linkByAccount.get(accountId);
                    if (!link)
                        continue;
                    for (const entryId of entryIds) {
                        linkOps.push(this.patch(`/msdyn_timeentries(${entryId})`, {
                            "msdyn_project@odata.bind": `/msdyn_projects(${link.projectId})`,
                            "msdyn_projectTask@odata.bind": `/msdyn_projecttasks(${link.projectTaskId})`,
                        }));
                    }
                }
                await Promise.all(linkOps);
            }
        }
        // ---- Status PATCH ----
        // Per-entry try/catch so a single rejected entry doesn't take down the
        // whole bulk submit. Detailed per-entry diagnostics go to the console
        // for ops; the user-facing throw is short and friendly.
        const failures = [];
        await Promise.all(ids.map(async (id) => {
            try {
                const payload = { [STATUS_COLUMN]: STATUS_TO_CODE.Submitted };
                if (staffAudit[id]) {
                    await this.writeStaffTransaction("PATCH", `/msdyn_timeentries(${id})`, payload, id, staffAudit[id].subject, staffAudit[id].notes, expectedVersions[id]);
                }
                else {
                    await this.patch(`/msdyn_timeentries(${id})`, payload, expectedVersions[id]);
                }
            }
            catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                failures.push({ id, message });
            }
        }));
        if (failures.length > 0) {
            // Console-only forensic snapshot. The OOB TimeEntryStateValidator plugin
            // rejects with bare `[0x80040265]` (operation not allowed) and no
            // innererror — Dataverse will not surface more detail for this code, so
            // a fresh read of each failing row's state is the only path to root
            // cause. Best-effort: if the state read itself fails (auth, deletion,
            // throttling), we still throw the friendly user message below.
            //
            // What to read for each failing entry:
            //   msdyn_entrystatus — decoded to a status name; confirms the row is
            //     still Draft and not already past us
            //   statecode — 0 active, 1 inactive (inactive rows can't transition)
            //   msdyn_duration — minutes; 0/negative is a known rejection cause
            //   _msdyn_bookableresource_value — submitter must equal the resource
            //     on the entry; FormattedValue shows the person's name
            //   _msdyn_workorder_value / _msdyn_project_value — confirms whether
            //     the v0.5.27 General Work bridge actually landed
            //   _msdyn_projecttask_value — some envs require a task under the
            //     project, not just the project; FormattedValue clarifies
            //   _msdyn_transactioncategory_value — service type (rate carrier)
            //   _crb7a_billableaccount_value — original account-direct link
            const failingIds = failures.map((f) => f.id);
            const stateClause = failingIds.map((id) => `msdyn_timeentryid eq ${id}`).join(" or ");
            try {
                const stateData = await this.get(`/msdyn_timeentries?$select=msdyn_timeentryid,${STATUS_COLUMN},msdyn_duration,statecode,_msdyn_bookableresource_value,_msdyn_workorder_value,_msdyn_project_value,_msdyn_projecttask_value,_msdyn_transactioncategory_value,_crb7a_billableaccount_value&$filter=${encodeURIComponent(stateClause)}&$top=500`);
                const stateById = new Map();
                for (const r of stateData.value)
                    stateById.set(r.msdyn_timeentryid, r);
                const tableRows = failures.map((f) => {
                    const s = stateById.get(f.id) ?? {};
                    const fv = (k) => s[`${k}@OData.Community.Display.V1.FormattedValue`];
                    const statusCode = s[STATUS_COLUMN];
                    return {
                        id: f.id,
                        error: f.message.split(" → ").pop(),
                        status: statusCode == null
                            ? "Draft(null)"
                            : STATUS_FROM_CODE[statusCode] ?? `unknown(${statusCode})`,
                        statecode: s.statecode,
                        duration_min: s.msdyn_duration,
                        resource: fv("_msdyn_bookableresource_value") ?? s._msdyn_bookableresource_value ?? "—",
                        project: fv("_msdyn_project_value") ?? s._msdyn_project_value ?? "—",
                        workorder: fv("_msdyn_workorder_value") ?? s._msdyn_workorder_value ?? "—",
                        projecttask: fv("_msdyn_projecttask_value") ?? s._msdyn_projecttask_value ?? "—",
                        service_type: fv("_msdyn_transactioncategory_value") ?? s._msdyn_transactioncategory_value ?? "—",
                        billable_account: fv("_crb7a_billableaccount_value") ?? s._crb7a_billableaccount_value ?? "—",
                    };
                });
                console.error("[Dataverse] submitEntries failed for the following entries:");
                console.table(tableRows);
            }
            catch (stateErr) {
                console.error("[Dataverse] submitEntries failures (state read also failed):", failures, stateErr);
            }
            throw new Error(failures.length === ids.length
                ? `Couldn't submit your entries. Please review and try again.`
                : `Couldn't submit ${failures.length} of ${ids.length} entries — the rest went through.`);
        }
    }
    // ---------- General Work bridge ----------
    // Ensures the (project, default task) pair exists for an account-direct
    // submit. Returns both ids so the caller can PATCH both lookups onto the
    // time entry — the OOB TimeEntryStateValidator plugin requires both for
    // Draft→Submitted (v0.5.29.2 forensic diagnostic confirmed project alone
    // is insufficient).
    async ensureGeneralWorkProjectAndTask(accountId) {
        const projectId = await this.ensureGeneralWorkProject(accountId);
        const projectTaskId = await this.ensureDefaultProjectTask(projectId);
        return { projectId, projectTaskId };
    }
    async ensureGeneralWorkProject(accountId) {
        // Look up an existing General Work project for this account. Subject is
        // the natural dedup key — the customer link tells us which account this
        // bucket belongs to.
        const existing = await this.get(`/msdyn_projects?$select=msdyn_projectid&$filter=msdyn_subject eq 'General Work' and _msdyn_customer_value eq ${accountId}&$top=1`);
        if (existing.value.length > 0) {
            return existing.value[0].msdyn_projectid;
        }
        // Create one. The customer lookup on msdyn_project is polymorphic
        // (account or contact), and Dataverse exposes one navigation property
        // per (column, target) pair for the bind notation. The exact name
        // varies by how the column was published, so we ask the metadata once
        // and cache the answer (see discoverNavProperty).
        //
        // We don't set msdyn_projectmanager: the PM-delegation approval queue
        // simply won't surface General Work entries, which is fine because
        // the primary approval path (submitter's manager / BU / Finance /
        // Admin) doesn't depend on project ownership.
        const navProp = await this.discoverNavProperty("msdyn_project", "msdyn_customer", "account");
        const created = await this.post(`/msdyn_projects`, {
            msdyn_subject: "General Work",
            [`${navProp}@odata.bind`]: `/accounts(${accountId})`,
        });
        return created.msdyn_projectid;
    }
    async ensureDefaultProjectTask(projectId) {
        // One default task per General Work project, named "General Work" too
        // so the UI is self-explanatory if a user ever lands on the row in
        // Dataverse. Dedup is by (project, subject) — same pattern as the
        // project lookup. msdyn_subject is the OOB title column on
        // msdyn_projecttask.
        const existing = await this.get(`/msdyn_projecttasks?$select=msdyn_projecttaskid&$filter=_msdyn_project_value eq ${projectId} and msdyn_subject eq 'General Work'&$top=1`);
        if (existing.value.length > 0) {
            return existing.value[0].msdyn_projecttaskid;
        }
        const navProp = await this.discoverNavProperty("msdyn_projecttask", "msdyn_project", "msdyn_project");
        const created = await this.post(`/msdyn_projecttasks`, {
            msdyn_subject: "General Work",
            [`${navProp}@odata.bind`]: `/msdyn_projects(${projectId})`,
        });
        return created.msdyn_projecttaskid;
    }
    // Discovers and caches the navigation property name for a
    // (entity, foreign-key column, target entity) triple. Dataverse's
    // EntityDefinitions endpoint returns the exact
    // ReferencingEntityNavigationPropertyName for each many-to-one
    // relationship, which is the literal string to put before
    // `@odata.bind` in a write payload. Read-only metadata call — anyone
    // with read on the entity can query it.
    async discoverNavProperty(entity, referencingAttribute, referencedEntity) {
        const key = `${entity}:${referencingAttribute}:${referencedEntity}`;
        if (NAV_PROP_CACHE[key])
            return NAV_PROP_CACHE[key];
        const data = await this.get(`/EntityDefinitions(LogicalName='${entity}')/ManyToOneRelationships?$filter=ReferencingAttribute eq '${referencingAttribute}' and ReferencedEntity eq '${referencedEntity}'&$select=ReferencingEntityNavigationPropertyName`);
        const navProp = data?.value?.[0]?.ReferencingEntityNavigationPropertyName;
        if (!navProp) {
            throw new Error(`[Dataverse] Couldn't discover the navigation property for ` +
                `${entity}.${referencingAttribute} → ${referencedEntity}. ` +
                `Verify in the maker portal that the lookup still targets ${referencedEntity}.`);
        }
        NAV_PROP_CACHE[key] = navProp;
        return navProp;
    }
    // ---------- Time Source ----------
    // msdyn_timeentry links to a Time Source record (msdyn_timeentrysetting) via
    // a lookup whose logical name in THIS env is `msdyn_timeentrysettingid`
    // (wire `_msdyn_timeentrysettingid_value`) — NOT the documented
    // `msdyn_timeentrysetting`, and not the option-set column `msdyn_timesource`
    // that v0.5.14 guessed (both 400 here; verified against raines.crm6
    // 2026-06-23). Each Time Source record carries a source type; Project Service
    // is the default record, so leaving the lookup empty (as the app did) made
    // every entry — work orders included — log as Project Service.
    //
    // We bind it explicitly: Work Order regarding → Field Service, everything
    // else → Project Service. The setting record id is resolved by its
    // source-type value (cached), and the @odata.bind navigation property is
    // discovered from metadata (same approach as the General Work bridge), so no
    // GUID or casing is hardcoded.
    async applyTimeSource(payload, regarding) {
        try {
            const sourceType = regarding?.type === "msdyn_workorder"
                ? TIME_SOURCE_TYPE.FieldService
                : TIME_SOURCE_TYPE.ProjectService;
            const settingId = await this.resolveTimeSourceSettingId(sourceType);
            if (!settingId)
                return; // settings unreadable — leave Dataverse's default
            const navProp = await this.discoverNavProperty("msdyn_timeentry", "msdyn_timeentrysettingid", "msdyn_timeentrysetting");
            payload[`${navProp}@odata.bind`] = `/msdyn_timeentrysettings(${settingId})`;
        }
        catch (err) {
            // Never let Time Source resolution break a time-entry write — fall back
            // to the default Time Source (Project Service) and log once for ops.
            console.warn("[Dataverse] Could not set Time Source; using default.", err);
        }
    }
    async resolveTimeSourceSettingId(sourceType) {
        if (!TIME_SOURCE_SETTING_IDS) {
            const data = await this.get(`/msdyn_timeentrysettings?$select=msdyn_timeentrysettingid,msdyn_sourcetype&$top=50`);
            const map = {};
            for (const r of data.value) {
                if (typeof r.msdyn_sourcetype === "number") {
                    map[r.msdyn_sourcetype] = r.msdyn_timeentrysettingid;
                }
            }
            TIME_SOURCE_SETTING_IDS = map;
        }
        return TIME_SOURCE_SETTING_IDS[sourceType];
    }
    // ---------- Audit reads ----------
    async listAnnotations(timeEntryId) {
        const data = await this.get(`/annotations?$select=annotationid,_objectid_value,objecttypecode,subject,notetext,createdon,_createdby_value&$filter=_objectid_value eq ${timeEntryId} and objecttypecode eq 'msdyn_timeentry'&$orderby=createdon desc&$top=200`);
        return data.value.map((a) => ({
            annotationid: a.annotationid,
            objectid: a._objectid_value,
            objecttypecode: "msdyn_timeentry",
            subject: a.subject ?? "",
            notetext: a.notetext ?? "",
            createdon: a.createdon,
            createdbyName: a["_createdby_value@OData.Community.Display.V1.FormattedValue"] ?? "",
        }));
    }
    // ---------- Approver queues ----------
    async listPendingForManager(managerUserId) {
        // 1. systemusers whose manager is the current user.
        const reports = await this.get(`/systemusers?$select=systemuserid&$filter=_parentsystemuserid_value eq ${managerUserId}&$top=500`);
        const reportIds = reports.value.map((u) => u.systemuserid);
        if (reportIds.length === 0)
            return [];
        // 2. their bookable resources.
        const resFilter = reportIds.map((id) => `_userid_value eq ${id}`).join(" or ");
        const resources = await this.get(`/bookableresources?$select=bookableresourceid&$filter=${encodeURIComponent(resFilter)}&$top=500`);
        const resourceIds = resources.value.map((r) => r.bookableresourceid);
        if (resourceIds.length === 0)
            return [];
        // 3. submitted entries for those resources.
        return this.queryEntriesForResources(resourceIds);
    }
    async listPendingForSelf(userId) {
        const resources = await this.get(`/bookableresources?$select=bookableresourceid&$filter=_userid_value eq ${userId}&$top=50`);
        const resourceIds = resources.value.map((r) => r.bookableresourceid);
        if (resourceIds.length === 0)
            return [];
        return this.queryEntriesForResources(resourceIds);
    }
    async listPendingDelegated(scope) {
        const base = statusFilter("Submitted");
        switch (scope.role) {
            case "Admin":
            case "Finance.Approver": {
                const data = await this.get(`/msdyn_timeentries?$select=${TIMEENTRY_FIELDS}&$filter=${encodeURIComponent(base)}&$orderby=msdyn_start desc&$top=500`);
                return data.value.map(toTimeEntry);
            }
            case "Operations.Manager": {
                const data = await this.get(`/bookableresources?$select=bookableresourceid&$expand=UserId($select=businessunitid)&$top=500`);
                const inBu = data.value
                    .filter((r) => r.UserId?._businessunitid_value === scope.businessUnitId)
                    .map((r) => r.bookableresourceid);
                if (inBu.length === 0)
                    return [];
                return this.queryEntriesForResources(inBu);
            }
            case "Project.Manager": {
                const projects = await this.get(`/msdyn_projects?$select=msdyn_projectid&$filter=_msdyn_projectmanager_value eq ${scope.managerUserId}&$top=500`);
                const projectIds = projects.value.map((p) => p.msdyn_projectid);
                if (projectIds.length === 0)
                    return [];
                const projFilter = projectIds
                    .map((id) => `_msdyn_project_value eq ${id}`)
                    .join(" or ");
                const filter = `${base} and (${projFilter})`;
                const data = await this.get(`/msdyn_timeentries?$select=${TIMEENTRY_FIELDS}&$filter=${encodeURIComponent(filter)}&$orderby=msdyn_start desc&$top=500`);
                return data.value.map(toTimeEntry);
            }
        }
    }
    // ---------- Internals ----------
    async queryEntriesForResources(resourceIds) {
        const orClause = resourceIds.map((id) => `_msdyn_bookableresource_value eq ${id}`).join(" or ");
        const filter = `${statusFilter("Submitted")} and (${orClause})`;
        const data = await this.get(`/msdyn_timeentries?$select=${TIMEENTRY_FIELDS}&$filter=${encodeURIComponent(filter)}&$orderby=msdyn_start desc&$top=500`);
        return data.value.map(toTimeEntry);
    }
    async writeAnnotation(timeEntryId, subject, notetext) {
        await this.post(`/annotations`, {
            subject,
            notetext,
            "objectid_msdyn_timeentry@odata.bind": `/msdyn_timeentries(${timeEntryId})`,
        });
    }
    async headers(extra = {}) {
        const token = await this.cfg.getToken();
        const h = new Headers({
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "OData-MaxVersion": "4.0",
            "OData-Version": "4.0",
            "Content-Type": "application/json; charset=utf-8",
            Prefer: 'odata.include-annotations="OData.Community.Display.V1.FormattedValue,Microsoft.Dynamics.CRM.lookuplogicalname"',
        });
        for (const [k, v] of Object.entries(extra))
            h.set(k, v);
        return h;
    }
    async get(path) {
        const res = await fetch(`${this.apiBase}${path}`, { headers: await this.headers() });
        return parse(res, "GET", path);
    }
    async writeStaffTransaction(method, path, payload, id, subject, notes, etag) {
        const batch = `batch_${crypto.randomUUID()}`;
        const changeSet = `changeset_${crypto.randomUUID()}`;
        const body = [
            `--${batch}`,
            `Content-Type: multipart/mixed; boundary=${changeSet}`,
            "",
            `--${changeSet}`,
            "Content-Type: application/http",
            "Content-Transfer-Encoding: binary",
            "Content-ID: 1",
            "",
            `${method} ${this.apiBase}${path} HTTP/1.1`,
            "Content-Type: application/json",
            "Prefer: return=representation",
            ...(etag ? [`If-Match: ${etag}`] : []),
            "",
            JSON.stringify(payload),
            `--${changeSet}`,
            "Content-Type: application/http",
            "Content-Transfer-Encoding: binary",
            "Content-ID: 2",
            "",
            `POST ${this.apiBase}/annotations HTTP/1.1`,
            "Content-Type: application/json",
            "",
            JSON.stringify({
                subject,
                notetext: notes,
                "objectid_msdyn_timeentry@odata.bind": `/msdyn_timeentries(${id})`,
            }),
            `--${changeSet}--`,
            `--${batch}--`,
            "",
        ].join("\r\n");
        const response = await fetch(`${this.apiBase}/$batch`, {
            method: "POST",
            headers: await this.headers({ "Content-Type": `multipart/mixed; boundary=${batch}` }),
            body,
        });
        const text = await response.text();
        const statuses = [...text.matchAll(/HTTP\/1\.[01] (\d{3})[^\r\n]*\r?\n/g)];
        const failed = statuses.find((match) => Number(match[1]) < 200 || Number(match[1]) >= 300);
        if (!response.ok || statuses.length !== 2 || failed) {
            const code = failed?.[1] ?? response.status;
            console.error("[Dataverse] Staff entry/audit transaction failed", { status: code, response: text });
            if (response.ok && !failed) {
                throw new Error("Dataverse returned an unrecognized staff transaction response. Refresh Staff Entries before retrying; the transaction may already have completed.");
            }
            throw new Error(Number(code) === 412
                ? "This staff entry changed while being saved (412). Refresh Staff Entries and reopen it before retrying."
                : `Could not save the staff entry and its audit history together (${code}). Check Dataverse permissions and refresh before retrying.`);
        }
        const first = statuses[0];
        const firstResponse = text.slice(first.index + first[0].length).split(/\r?\n--/)[0];
        const separator = firstResponse.search(/\r?\n\r?\n/);
        if (separator < 0)
            throw new Error("Dataverse saved the staff transaction but returned no entry representation. Refresh Staff Entries before continuing.");
        const json = firstResponse.slice(separator).trim();
        let row;
        try {
            row = JSON.parse(json);
        }
        catch (error) {
            console.error("[Dataverse] Could not read the saved staff entry", error);
            throw new Error("Dataverse saved the staff transaction but returned an unreadable entry representation. Refresh Staff Entries before continuing.");
        }
        if (!row || row.msdyn_timeentryid !== id) {
            throw new Error("Dataverse saved the staff transaction but returned an unrecognized entry representation. Refresh Staff Entries before continuing.");
        }
        return toTimeEntry(row);
    }
    async getAllRows(path) {
        const rows = [];
        let next = path;
        while (next) {
            const data = await this.get(next);
            rows.push(...data.value);
            const link = data["@odata.nextLink"];
            next = undefined;
            if (typeof link === "string") {
                const url = new URL(link, `${this.apiBase}/`);
                if (!url.href.startsWith(`${this.apiBase}/`)) {
                    throw new Error("Dataverse returned an unexpected paging URL.");
                }
                next = url.href.slice(this.apiBase.length);
            }
        }
        return rows;
    }
    async post(path, body) {
        const res = await fetch(`${this.apiBase}${path}`, {
            method: "POST",
            headers: await this.headers({ Prefer: "return=representation" }),
            body: JSON.stringify(body),
        });
        return parse(res, "POST", path);
    }
    async patch(path, body, etag) {
        const res = await fetch(`${this.apiBase}${path}`, {
            method: "PATCH",
            headers: await this.headers({ Prefer: "return=representation", ...(etag ? { "If-Match": etag } : {}) }),
            body: JSON.stringify(body),
        });
        return parse(res, "PATCH", path);
    }
    async del(path) {
        const res = await fetch(`${this.apiBase}${path}`, {
            method: "DELETE",
            headers: await this.headers(),
        });
        if (!res.ok && res.status !== 204) {
            throw new Error(`[Dataverse] DELETE ${path} → ${res.status} ${await res.text()}`);
        }
    }
}
// ------------------------ Helpers -----------------------------------
const TIMEENTRY_FIELDS = [
    "msdyn_timeentryid",
    "msdyn_description",
    "msdyn_start",
    "msdyn_end",
    "msdyn_duration",
    "msdyn_type",
    STATUS_COLUMN,
    "crb7a_detailsofwork",
    "msdyn_externaldescription",
    // WHMCS posting state — Yes/No, text, datetime. See domain/index.ts.
    "crb7a_whmcsposted",
    "crb7a_whmcsbillableitemid",
    "crb7a_whmcspostedon",
    "_msdyn_bookableresource_value",
    "_msdyn_workorder_value",
    "_msdyn_project_value",
    "_msdyn_projecttask_value",
    "_msdyn_transactioncategory_value",
    "_msdyn_resourcecategory_value",
    "_msdyn_bookableresourcebooking_value",
    "_crb7a_billableaccount_value",
    // Time Source lookup — env-specific logical name (see applyTimeSource).
    "_msdyn_timeentrysettingid_value",
].join(",");
function toTimeEntry(t) {
    return {
        msdyn_timeentryid: t.msdyn_timeentryid,
        msdyn_description: t.msdyn_description ?? "",
        msdyn_start: t.msdyn_start,
        msdyn_end: t.msdyn_end,
        msdyn_duration: durationMinutes(t),
        msdyn_type: TYPE_FROM_CODE[t.msdyn_type] ?? "Work",
        // Null reads as Draft for entries created before the custom column was
        // added (legacy rows). Same null-as-Draft semantics as statusFilter().
        msdyn_entryStatus: STATUS_FROM_CODE[t[STATUS_COLUMN]] ?? "Draft",
        bookableresource: t._msdyn_bookableresource_value,
        regardingobjectid: extractRegarding(t),
        msdyn_transactioncategoryid: t._msdyn_transactioncategory_value ?? undefined,
        resourceCategoryId: t._msdyn_resourcecategory_value ?? undefined,
        msdyn_projecttaskid: t._msdyn_projecttask_value ?? undefined,
        msdyn_bookableresourcebooking: t._msdyn_bookableresourcebooking_value ?? undefined,
        // Time Source — read back from the msdyn_timeentrysetting lookup via its
        // FormattedValue (the setting record's name: "Field Service" /
        // "Project Service"). v0.5.16 concluded this couldn't be wired; v0.6.4 sets
        // it via the msdyn_timeentrysettingid lookup — see applyTimeSource.
        msdyn_timesource: timeSourceFromSettingName(t["_msdyn_timeentrysettingid_value@OData.Community.Display.V1.FormattedValue"]),
        crb7a_detailsofwork: t.crb7a_detailsofwork ?? undefined,
        msdyn_externaldescription: t.msdyn_externaldescription ?? undefined,
        crb7a_whmcsposted: t.crb7a_whmcsposted ?? undefined,
        crb7a_whmcsbillableitemid: t.crb7a_whmcsbillableitemid ?? undefined,
        crb7a_whmcspostedon: t.crb7a_whmcspostedon ?? undefined,
    };
}
function extractRegarding(t) {
    // msdyn_timeentry has no OOB polymorphic regardingobjectid lookup (confirmed
    // on raines.crm6.dynamics.com — `_regardingobjectid_value` 400s as "property
    // not found"). To preserve account-direct entries we keep the custom
    // `crb7a_BillableAccount` lookup-to-account on msdyn_timeentry; work orders
    // and projects still resolve their owning account upstream via the OOB
    // chain (msdyn_serviceaccount / msdyn_customer — see clientResolver).
    if (t._msdyn_workorder_value) {
        return {
            type: "msdyn_workorder",
            id: t._msdyn_workorder_value,
            display: t["_msdyn_workorder_value@OData.Community.Display.V1.FormattedValue"] ?? "",
        };
    }
    if (t._msdyn_project_value) {
        return {
            type: "msdyn_project",
            id: t._msdyn_project_value,
            display: t["_msdyn_project_value@OData.Community.Display.V1.FormattedValue"] ?? "",
        };
    }
    if (t._crb7a_billableaccount_value) {
        return {
            type: "account",
            id: t._crb7a_billableaccount_value,
            display: t["_crb7a_billableaccount_value@OData.Community.Display.V1.FormattedValue"] ?? "",
        };
    }
    return undefined;
}
function toCreatePayload(input) {
    const p = {
        msdyn_description: input.msdyn_description,
        msdyn_start: input.msdyn_start,
        msdyn_end: input.msdyn_end,
        msdyn_type: TYPE_TO_CODE[input.msdyn_type],
        [STATUS_COLUMN]: STATUS_TO_CODE[input.msdyn_entryStatus],
        "msdyn_bookableresource@odata.bind": `/bookableresources(${input.bookableresource})`,
    };
    if (typeof input.msdyn_duration === "number")
        p.msdyn_duration = input.msdyn_duration;
    if (input.crb7a_detailsofwork !== undefined) {
        p.crb7a_detailsofwork = input.crb7a_detailsofwork;
    }
    if (input.msdyn_externaldescription !== undefined) {
        p.msdyn_externaldescription = input.msdyn_externaldescription;
    }
    if (input.msdyn_transactioncategoryid) {
        p["msdyn_transactioncategory@odata.bind"] = `/msdyn_transactioncategories(${input.msdyn_transactioncategoryid})`;
    }
    if (input.resourceCategoryId) {
        p["msdyn_resourceCategory@odata.bind"] = `/bookableresourcecategories(${input.resourceCategoryId})`;
    }
    if (input.msdyn_projecttaskid) {
        p["msdyn_projectTask@odata.bind"] = `/msdyn_projecttasks(${input.msdyn_projecttaskid})`;
    }
    if (input.msdyn_bookableresourcebooking) {
        p["msdyn_BookableResourceBooking@odata.bind"] = `/bookableresourcebookings(${input.msdyn_bookableresourcebooking})`;
    }
    if (input.regardingobjectid)
        bindRegarding(p, input.regardingobjectid);
    return p;
}
function toPatchPayload(patch) {
    const p = {};
    if (patch.msdyn_description !== undefined)
        p.msdyn_description = patch.msdyn_description;
    if (patch.msdyn_start !== undefined)
        p.msdyn_start = patch.msdyn_start;
    if (patch.msdyn_end !== undefined)
        p.msdyn_end = patch.msdyn_end;
    if (patch.msdyn_duration !== undefined)
        p.msdyn_duration = patch.msdyn_duration;
    if (patch.msdyn_type)
        p.msdyn_type = TYPE_TO_CODE[patch.msdyn_type];
    if (patch.msdyn_entryStatus)
        p[STATUS_COLUMN] = STATUS_TO_CODE[patch.msdyn_entryStatus];
    if (patch.crb7a_detailsofwork !== undefined)
        p.crb7a_detailsofwork = patch.crb7a_detailsofwork;
    if (patch.msdyn_externaldescription !== undefined)
        p.msdyn_externaldescription = patch.msdyn_externaldescription;
    if (patch.bookableresource)
        p["msdyn_bookableresource@odata.bind"] = `/bookableresources(${patch.bookableresource})`;
    if ("msdyn_transactioncategoryid" in patch)
        p["msdyn_transactioncategory@odata.bind"] = patch.msdyn_transactioncategoryid ? `/msdyn_transactioncategories(${patch.msdyn_transactioncategoryid})` : null;
    if ("resourceCategoryId" in patch)
        p["msdyn_resourceCategory@odata.bind"] = patch.resourceCategoryId ? `/bookableresourcecategories(${patch.resourceCategoryId})` : null;
    if ("msdyn_projecttaskid" in patch)
        p["msdyn_projectTask@odata.bind"] = patch.msdyn_projecttaskid ? `/msdyn_projecttasks(${patch.msdyn_projecttaskid})` : null;
    if ("msdyn_bookableresourcebooking" in patch)
        p["msdyn_BookableResourceBooking@odata.bind"] = patch.msdyn_bookableresourcebooking ? `/bookableresourcebookings(${patch.msdyn_bookableresourcebooking})` : null;
    if ("regardingobjectid" in patch) {
        bindRegarding(p, patch.regardingobjectid);
    }
    return p;
}
function bindRegarding(payload, ref) {
    // Clear all three targets first so switching regarding type drops the old
    // binding cleanly. Account-direct uses the custom `crb7a_BillableAccount`
    // lookup-to-account on msdyn_timeentry — there's no OOB polymorphic
    // regardingobjectid on this entity, so the custom column stays.
    payload["msdyn_WorkOrder@odata.bind"] = null;
    payload["msdyn_project@odata.bind"] = null;
    payload["crb7a_BillableAccount_account@odata.bind"] = null;
    if (ref?.type === "msdyn_workorder") {
        payload["msdyn_WorkOrder@odata.bind"] = `/msdyn_workorders(${ref.id})`;
    }
    else if (ref?.type === "msdyn_project") {
        payload["msdyn_project@odata.bind"] = `/msdyn_projects(${ref.id})`;
    }
    else if (ref?.type === "account") {
        payload["crb7a_BillableAccount_account@odata.bind"] = `/accounts(${ref.id})`;
    }
}
function clientIdFor(entry, woAccount, projAccount) {
    const r = entry.regardingobjectid;
    if (!r)
        return;
    if (r.type === "account")
        return r.id;
    if (r.type === "msdyn_workorder")
        return woAccount.get(r.id);
    if (r.type === "msdyn_project")
        return projAccount.get(r.id) ?? undefined;
}
function timeSourceFromSettingName(name) {
    // The two Time Source records are named "Field Service" / "Project Service".
    if (!name)
        return undefined;
    return name === "Field Service" ? "FieldService" : "ProjectService";
}
function invert(m) {
    const out = {};
    for (const k in m)
        out[m[k]] = k;
    return out;
}
async function parse(res, method, path) {
    if (res.status === 204)
        return null;
    const text = await res.text();
    if (!res.ok) {
        // Dataverse error envelopes nest: { error: { code, message, innererror: { message, type, stacktrace } } }
        // Plugins typically put the human-readable reason in innererror.message and
        // a hresult/type in innererror.type — bubble both up so the console shows
        // the real cause instead of just the top-level summary.
        let detail = text;
        try {
            const j = JSON.parse(text);
            const code = j.error?.code ? ` [${j.error.code}]` : "";
            const top = j.error?.message ?? "";
            const inner = j.error?.innererror?.message;
            const innerType = j.error?.innererror?.type;
            const innerBits = [inner, innerType].filter(Boolean).join(" — ");
            detail = innerBits ? `${top}${code} (${innerBits})` : `${top}${code}`;
            if (!detail.trim())
                detail = text;
        }
        catch {
            /* fall through with raw text */
        }
        throw new Error(`[Dataverse] ${method} ${path} → ${res.status} ${detail}`);
    }
    return text ? JSON.parse(text) : null;
}
